// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Pagination, { PaginationPresentationProps } from "@insite/mobius/Pagination";
import loadReorderPad, { defaultReorderPadParameters } from "@steiner/App/Handlers/ReorderPad/LoadReorderPad";
import loadOpenBids, { defaultParameters } from "@steiner/App/Handlers/XNG/LoadOpenBids";
import { CustomAppState } from "@steiner/App/Store/Reducers";
import { XNGAGE_ELEMENTS } from "@steiner/XDL/constants";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

const mapStateToProps = (state: CustomAppState) => ({
    pagination: state.xReorderPad.pagination,
    searchParams: state.xReorderPad.searchParams,
});

const mapDispatchToProps = {
    loadReorderPad,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ReorderPadPaginationStyles {
    pagination?: PaginationPresentationProps;
}

export const reorderPadPaginationStyles: ReorderPadPaginationStyles = {};

const styles = reorderPadPaginationStyles;

const ReorderPadPagination = ({ searchParams, pagination, loadReorderPad }: Props) => {
    const [shouldScrollToTop, setShouldScrollToTop] = useState(false);

    useEffect(() => {
        if (shouldScrollToTop) {
            window.scrollTo(0, 0);
            setShouldScrollToTop(false);
        }
    }, [pagination?.page]);

    if (!pagination || !pagination.totalItemCount) {
        return null;
    }

    const changePage = (newPageIndex: number) => {
        setShouldScrollToTop(true);
        loadReorderPad({
            ...defaultReorderPadParameters,
            ...searchParams,
            page: newPageIndex,
            pageSize: pagination.pageSize,
        });
    };

    const changeResultsPerPage = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newPageSize = parseInt(event.currentTarget.value, 10);
        loadReorderPad({
            ...defaultReorderPadParameters,
            ...searchParams,
            page: 1,
            pageSize: newPageSize,
        });
    };

    return (
        <Pagination
            {...styles.pagination}
            resultsCount={pagination.totalItemCount}
            currentPage={pagination.page}
            resultsPerPage={pagination.pageSize}
            resultsPerPageOptions={pagination.pageSizeOptions}
            onChangePage={changePage}
            onChangeResultsPerPage={changeResultsPerPage}
            pageSizeCookie="reorderPad-PageSize"
            data-test-selector="reorderPad_pagination"
            prevPage={pagination.prevPageUri}
            nextPage={pagination.nextPageUri}
        />
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ReorderPadPagination),
    definition: {
        group: XNGAGE_ELEMENTS,
        displayName: "Reorder Pad Pagination",
        allowedContexts: ["ReorderPadPage"],
    },
};

export default widgetModule;
